const splitCamel = (value: string) =>
  value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();

const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const ENTITY_LABELS: Record<string, string> = {
  productVariant: "Product variant",
  product: "Product",
  earning: "Earning",
  admin: "Admin",
  reseller: "Reseller",
  refund: "Refund",
  order: "Order",
  banner: "Banner",
  coupon: "Coupon",
  specialOffer: "Special offer",
  delivery: "Delivery",
  aiAssistant: "AI assistant"
};

const VERB_LABELS: Record<string, string> = {
  pay: "payout sent",
  payMany: "payouts sent (bulk)",
  payAll: "payouts sent (all)",
  cancel: "canceled",
  cancelMany: "canceled (bulk)",
  priceChange: "price changed",
  commissionChange: "commission changed",
  discountChange: "discount changed",
  nameChange: "name changed",
  mediaChange: "preview changed",
  imageAdd: "image added",
  imageDelete: "image deleted",
  imageReorder: "images reordered",
  otherChange: "details changed",
  descriptionChange: "description changed",
  update: "updated",
  block: "blocked",
  unblock: "unblocked",
  createModerator: "moderator created",
  updateModerator: "moderator updated",
  deleteModerator: "moderator deleted",
  restore: "restored",
  activate: "activated",
  deactivate: "deactivated",
  create: "created",
  issue: "issued",
  feeChange: "fee changed"
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  ProductVariant: "Product variant",
  Reseller: "Reseller",
  Admin: "Admin",
  Earning: "Earning",
  Product: "Product",
  Refund: "Refund",
  Order: "Order"
};

/**
 * Turns a raw audited action into a readable phrase.
 *   "productVariant.otherChange" -> "Product variant — details changed"
 *   "earning.pay"                -> "Earning — payout sent"
 * Request-level rows ("PATCH /admin/...") are returned as-is.
 */
export const humanizeAuditAction = (action?: string | null): string => {
  if (!action) return "—";

  const isEntityLevel =
    action.includes(".") && !action.includes(" ") && !action.includes("/");

  if (!isEntityLevel) return action;

  const [rawEntity, rawVerb = ""] = action.split(".");
  const entity = ENTITY_LABELS[rawEntity] ?? capitalize(splitCamel(rawEntity));
  const verb = VERB_LABELS[rawVerb] ?? splitCamel(rawVerb);

  return verb ? `${entity} — ${verb}` : entity;
};

export const humanizeEntityType = (entityType?: string | null): string => {
  if (!entityType) return "—";
  return ENTITY_TYPE_LABELS[entityType] ?? capitalize(splitCamel(entityType));
};

/** "allowSellingBelowBuyingPrice" -> "Allow selling below buying price" */
export const humanizeFieldName = (key: string): string =>
  key ? capitalize(splitCamel(key)) : key;

export const shortId = (value?: string | null) =>
  value ? `${value.slice(0, 8)}…` : "—";
